import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { getAuthHeader } from "../utils/auth"; // ★ 1. 認証ヘッダー取得関数をインポート
import { formatJstDateTime, isEnglishOperatorMode } from "../utils/operatorMode";

// --- SVG Icons ---
const DownloadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>;
const ChevronDownIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>;
const ChevronUpIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>;
const LockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-slate-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>;
const LoadingSpinner = () => (
    <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);
const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;


// --- Pagination Component ---
function Pagination({ currentPage, totalPages, onPageChange, disabled }) {
    if (totalPages <= 1) return null;
    return (
        <div className="flex flex-wrap items-center justify-center gap-2">
            <button onClick={() => onPageChange(1)} disabled={currentPage === 1 || disabled} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">最初</button>
            <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1 || disabled} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">前へ</button>
            <span className="text-sm text-gray-700">{currentPage} / {totalPages}</span>
            <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages || disabled} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">次へ</button>
            <button onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages || disabled} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">最後</button>
        </div>
    );
}

// ★ アップグレードモーダルコンポーネント (🚀 修正: userを受け取る)
function UpgradeModal({ onClose, user }) {
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white p-8 rounded-lg shadow-2xl border w-full max-w-md text-center relative"
        onClick={e => e.stopPropagation()}
      >
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="閉じる"
        >
          <CloseIcon />
        </button>
        
        <LockIcon />
        
        <h3 className="text-xl font-bold text-slate-800 mt-2">
          この機能は有料プランでご利用いただけます
        </h3>
        <p className="text-slate-600 mt-4">
          全期間の履歴閲覧、ページ遷移、CSVダウンロード、案件管理の全機能を利用するには、プランのアップグレードが必要です。
        </p>
        
        {/* 🚀 修正: OEMユーザーの場合は、本家アカウントページへのリンクを隠し、問い合わせを促す */}
        {!user?.oem_id ? (
          <Link 
            to="/account" 
            className="mt-8 inline-block bg-green-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-green-700 transition-colors w-full"
          >
            🚀 プランをアップグレードする
          </Link>
        ) : (
          <div className="mt-8 p-4 bg-blue-50 text-blue-700 font-bold rounded-xl border border-blue-100">
            アップグレードについては、ご契約先の担当者までお問い合わせください。
          </div>
        )}
      </div>
    </div>
  );
}


function ClickLogsPage() {
  const { user } = useOutletContext();
  const isEnglishBeta = isEnglishOperatorMode();
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [detailedTimestamps, setDetailedTimestamps] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isFreePlan, setIsFreePlan] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const API_URL = window.location.hostname === 'localhost' 
    ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
    : window.location.origin;

  const selectedClientId = useMemo(() => (user?.role === "admin" || user?.role === "admin_staff" ? localStorage.getItem("selectedClientId") || "" : user?.client_id), [user]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setCurrentPage(1);
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    if (!selectedClientId) {
      setIsLoading(false);
      return;
    }

    const fetchSubscription = async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscription-status?clientId=${selectedClientId}`, {
          headers: getAuthHeader(),
        });
        if (!res.ok) throw new Error(isEnglishBeta ? 'Could not load plan information.' : 'プラン情報の取得に失敗しました。');
        const data = await res.json();
        if (data.success) {
          const isFree = !data.subscription || data.subscription.plan_id_string === 'free';
          setIsFreePlan(isFree);
        } else {
          throw new Error(data.error || (isEnglishBeta ? 'Could not load plan information.' : 'プラン情報の取得に失敗しました。'));
        }
      } catch (error) {
        console.error("プラン情報の取得に失敗:", error);
      }
    };

    fetchSubscription();
  }, [API_URL, selectedClientId, isEnglishBeta]);

  const fetchLogs = useCallback((page, keyword = "") => {
    if (!selectedClientId) return;
    const params = new URLSearchParams({
      client_id: selectedClientId,
      page: String(page),
      limit: "50",
    });
    if (keyword) {
      params.set("searchTerm", keyword);
    }

    setIsLoading(true);
    fetch(`${API_URL}/api/click-logs?${params.toString()}`, {
      headers: getAuthHeader(),
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setLogs(data.logs || []);
          setPagination(data.pagination || null);
        } else {
          console.error("クリックログの取得に失敗しました:", data.error);
          setLogs([]);
          setPagination(null);
        }
      })
      .catch(error => {
        console.error("クリックログの取得中にエラーが発生しました:", error);
        setLogs([]);
        setPagination(null);
      })
      .finally(() => setIsLoading(false));
  }, [API_URL, selectedClientId]);

  useEffect(() => {
    if (!selectedClientId) return;
    fetchLogs(currentPage, debouncedSearchTerm);
  }, [selectedClientId, currentPage, debouncedSearchTerm, fetchLogs]);

  const downloadCSV = () => {
    if (isFreePlan) {
        setShowUpgradeModal(true);
        return;
    }
    if (logs.length === 0) return alert("ダウンロードするデータがありません。");
    
    const headers = isEnglishBeta
      ? ["Company", "Engaged URL", "Operator email", "Event count", "Latest event time (JST)", "List source"]
      : ["会社名", "クリックされたURL", "担当者メール", "クリック回数", "最新クリック日時", "リスト種別"];
    const rows = logs.map(log => [
        log.company, log.url, log.staff_email,
        log.click_count, isEnglishBeta ? formatJstDateTime(log.latest_click_at) : new Date(log.latest_click_at).toLocaleString('ja-JP'),
        log.list_type === 'client' ? (isEnglishBeta ? 'My list' : 'マイリスト') : (isEnglishBeta ? 'SalesBreaker DB' : '弊社提供')
    ]);
    const csvData = [headers, ...rows].map(row => row.map(val => `"${String(val || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    
    const blob = new Blob(["\uFEFF" + csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "click_logs_summary.csv";
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
  };
  
  // capability freeze 2026-08-15: クリックログの閲覧（ページ送り・詳細展開）は無料プランにも開放。
  // CSVダウンロード（持ち出し）のみ有料限定を維持（downloadCSV側のゲート）。
  const handlePageChange = (newPage) => {
    if (newPage > 0 && pagination && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const toggleExpand = async (log) => {

    // ★ 修正: 行の識別子を log.id から ユニークな複合キーに変更
    const trackingKey = log.tracking_id || 'legacy';
    const uniqueKey = `${log.sales_list_id}-${log.list_type}-${trackingKey}-${log.url}`;
    
    const newSet = new Set(expandedRows);

    // ★ 修正: 判定も uniqueKey を使用
    if (newSet.has(uniqueKey)) {
      newSet.delete(uniqueKey);
    } else {
      newSet.add(uniqueKey);
      if (!detailedTimestamps[uniqueKey]) {
          const params = new URLSearchParams({
              client_id: selectedClientId,
              list_type: log.list_type,
          });
          if (log.tracking_id) {
              params.set("tracking_id", log.tracking_id);
          }
          fetch(`${API_URL}/api/deals/click-logs/${log.sales_list_id}?${params.toString()}`, {
              headers: getAuthHeader(),
          })
              .then(res => res.json())
              .then(data => {
                  if (data.success) {
                      setDetailedTimestamps(prevDetails => ({
                          ...prevDetails,
                      [uniqueKey]: data.logs.map(t => isEnglishBeta ? formatJstDateTime(t.timestamp) : new Date(t.timestamp).toLocaleString('ja-JP'))
                      }));
                  }
              });
      }
    }
    setExpandedRows(newSet);
  };

  if (isLoading) {
    return (
        <div className="flex items-center justify-center h-[80vh]">
            <div className="flex flex-col items-center">
                <LoadingSpinner />
                <p className="text-lg font-semibold text-gray-700 mt-4">{isEnglishBeta ? "Loading engagement events..." : "クリックログを読み込んでいます..."}</p>
            </div>
        </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col justify-between gap-4 mb-6 sm:flex-row sm:items-center">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{isEnglishBeta ? "Engagement Events" : "🕵️‍♀️ クリックログ"}</h1>
            {isEnglishBeta && <p className="mt-2 text-sm text-gray-600">Click and visit signals for follow-up prioritization. All timestamps are displayed in JST.</p>}
          </div>
          <button onClick={downloadCSV} disabled={isFreePlan} className="flex w-full items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 transition-colors sm:w-auto disabled:bg-gray-400 disabled:cursor-not-allowed">
            <DownloadIcon /> {isEnglishBeta ? "Export visible events" : "表示中リストをCSV出力"}
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden relative">
           <div className="p-4 border-b">
            <input 
              type="text" 
              placeholder={isEnglishBeta ? "Search by company, URL, or operator..." : "会社名、URL、担当者で検索..."} 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 border rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>
          <div className="overflow-x-auto">
            <div className="divide-y divide-gray-100 md:hidden">
              {logs.length > 0 ? logs.map((log) => {
                const trackingKey = log.tracking_id || 'legacy';
                const rowKey = `${log.sales_list_id}-${log.list_type}-${trackingKey}-${log.url}`;
                return (
                  <div key={`${rowKey}-mobile`} className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 break-words">{log.company}</p>
                        <a href={log.url} target="_blank" rel="noopener noreferrer" className="mt-1 block text-xs text-blue-600 hover:underline break-all">{log.url}</a>
                      </div>
                      <button onClick={() => toggleExpand(log)} className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200">
                        {expandedRows.has(rowKey) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                      </button>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                      <span className="rounded-full bg-blue-50 px-2 py-1 font-semibold text-blue-700">{isEnglishBeta ? `${log.click_count} events` : `${log.click_count}回クリック`}</span>
                      {log.list_type === 'client' ? (
                        <span className="rounded-full bg-purple-50 px-2 py-1 font-semibold text-purple-700">{isEnglishBeta ? "My list" : "マイリスト"}</span>
                      ) : (
                        <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-600">{isEnglishBeta ? "SalesBreaker DB" : "弊社提供"}</span>
                      )}
                    </div>
                    <div className="mt-3 space-y-1 text-xs text-gray-500">
                      <p className="break-all">{log.staff_email}</p>
                      <p>{isEnglishBeta ? formatJstDateTime(log.latest_click_at) : new Date(log.latest_click_at).toLocaleString('ja-JP')}</p>
                    </div>
                    {expandedRows.has(rowKey) && (
                      <div className="mt-4 rounded-lg bg-gray-50 p-3">
                        <h4 className="font-semibold text-gray-700 mb-2">{isEnglishBeta ? "All event timestamps:" : "全クリック日時:"}</h4>
                        <ul className="max-h-40 list-disc list-inside overflow-y-auto rounded-md bg-white p-2 text-xs">
                          {detailedTimestamps[rowKey] ? (
                            detailedTimestamps[rowKey].map((timestamp, i) => (
                              <li key={i}>{timestamp}</li>
                            ))
                          ) : ( <li>{isEnglishBeta ? "Loading..." : "読み込み中..."}</li> )}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              }) : (
                <div className="px-4 py-10 text-center text-sm text-gray-500">{isEnglishBeta ? "No engagement events found." : "クリックログはありません。"}</div>
              )}
            </div>
            <table className="hidden w-full text-sm text-left text-gray-600 md:table">
              <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                <tr>
                  <th className="py-3 px-4">{isEnglishBeta ? "Company" : "会社名"}</th>
                  <th className="py-3 px-4">{isEnglishBeta ? "Engaged URL" : "クリックされたURL"}</th>
                  <th className="py-3 px-4">{isEnglishBeta ? "List source" : "リスト種別"}</th>
                  <th className="py-3 px-4">{isEnglishBeta ? "Operator" : "担当者"}</th>
                  <th className="py-3 px-4">{isEnglishBeta ? "Event count" : "クリック回数"}</th>
                  <th className="py-3 px-4">{isEnglishBeta ? "Latest event time (JST)" : "最新クリック日時"}</th>
                  <th className="py-3 px-4">{isEnglishBeta ? "Details" : "詳細"}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {logs.length > 0 ? logs.map((log) => {
                  // ★ 修正: マップ内で一意なキーを生成して使用
                  const trackingKey = log.tracking_id || 'legacy';
                  const rowKey = `${log.sales_list_id}-${log.list_type}-${trackingKey}-${log.url}`;
                  
                  return (
                    <React.Fragment key={rowKey}>
                      <tr className="hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">{log.company}</td>
                        <td className="py-3 px-4"><a href={log.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{log.url}</a></td>
                        <td className="py-3 px-4">
                          {log.list_type === 'client' ? (
                            <span className="font-semibold text-purple-700">{isEnglishBeta ? "My list" : "マイリスト"}</span>
                          ) : (
                            <span className="text-gray-500">{isEnglishBeta ? "SalesBreaker DB" : "弊社提供"}</span>
                          )}
                        </td>
                        <td className="py-3 px-4">{log.staff_email}</td>
                        <td className="py-3 px-4 font-semibold">{log.click_count}</td>
                        <td className="py-3 px-4">{isEnglishBeta ? formatJstDateTime(log.latest_click_at) : new Date(log.latest_click_at).toLocaleString('ja-JP')}</td>
                        <td className="py-3 px-4">
                          <button onClick={() => toggleExpand(log)} className="p-2 text-gray-600 hover:bg-gray-200 rounded-full disabled:opacity-50 disabled:cursor-not-allowed">
                            {/* ★ 修正: 判定も rowKey (uniqueKey) を使用 */}
                            {expandedRows.has(rowKey) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                          </button>
                        </td>
                      </tr>
                      {/* ★ 修正: 判定も rowKey (uniqueKey) を使用 */}
                      {expandedRows.has(rowKey) && (
                        <tr>
                          <td colSpan="7" className="p-4 bg-gray-50">
                            <h4 className="font-semibold text-gray-700 mb-2">{isEnglishBeta ? "All event timestamps:" : "全クリック日時:"}</h4>
                            <ul className="text-xs list-disc list-inside bg-white p-2 rounded-md max-h-40 overflow-y-auto">
                              {detailedTimestamps[rowKey] ? (
                                  detailedTimestamps[rowKey].map((timestamp, i) => (
                                      <li key={i}>{timestamp}</li>
                                  ))
                              ) : ( <li>{isEnglishBeta ? "Loading..." : "読み込み中..."}</li> )}
                            </ul>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                }) : (
                  <tr><td colSpan="7" className="text-center py-10 text-gray-500">{isEnglishBeta ? "No engagement events found." : "クリックログはありません。"}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm text-gray-600">{pagination ? (isEnglishBeta ? `${pagination.totalItems.toLocaleString()} events` : `全 ${pagination.totalItems.toLocaleString()} 件`) : ''}</span>
            <Pagination currentPage={currentPage} totalPages={pagination ? pagination.totalPages : 0} onPageChange={handlePageChange} />
          </div>
        </div>
      </div>
      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} user={user} />}
    </div>
  );
}

export default ClickLogsPage;
